import { useState, useRef, useEffect } from 'react';
import './Chatbot.scss';
import { chatFlowData } from './chatFlowData';
import { IconButton } from '@mui/material';
import { Call,HelpOutlined } from '@mui/icons-material';
import { get, post } from '../../Utilities';
import { connect } from 'react-redux';
import thumbnailImage from '../../assets/images/Courses/thumbnail.png';
import { getClientId, getClientUserId } from '../../redux/reducers/clientUserId.reducer';
import { chatbotActions } from '../../redux/actions/chatbot.action';
import { getChatMessages } from '../../redux/reducers/chatbot.reducer';
import VideoShow from '../Common/VideoPlayer/DrmVideoplayer/VideoShow';
import YouTube from 'react-youtube';

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildInitialFormData = (formConfig) =>
  formConfig.fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {});

const validateField = (field, value, isHindi) => {
  const v = field.validation || {};
  const label = isHindi && field.hindi_label ? field.hindi_label : field.label;
  if (v.required && !String(value).trim())
    return isHindi ? `${label} ज़रूरी है` : `${label} is required`;
  if (value && v.min !== undefined && Number(value) <= 0)
    return (
      (isHindi ? v.hindi_minMessage : v.minMessage) ||
      (isHindi ? `${label} सही होना चाहिए` : `${label} must be greater than 0`)
    );
  if (value && v.pattern && !v.pattern.test(String(value).trim()))
    return (
      (isHindi ? v.hindi_patternMessage : v.patternMessage) ||
      (isHindi ? `${label} सही नहीं है` : `${label} is invalid`)
    );
  return null;
};

const validateForm = (formConfig, formData, isHindi) => {
  const errors = {};
  formConfig.fields.forEach((field) => {
    const error = validateField(field, formData[field.name], isHindi);
    if (error) errors[field.name] = error;
  });
  return errors;
};

// ─── Language helper ───────────────────────────────────────────────────────────

const t = (node, key, isHindi) => {
  if (!node) return '';
  const hindiKey = `hindi_${key}`;
  if (isHindi && node[hindiKey]) return node[hindiKey];
  return node[key] || '';
};

// ─── Component ----

const Chatbot = (props) => {
  const { clientId, clientUserId, history, setChatMessagesToStore, chatMessages, isWrapper } =
    props;

  const [messages, setMessages] = useState(chatMessages);
  const [currentNodeId, setCurrentNodeId] = useState('languageSelect');
  const [isTyping, setIsTyping] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isFaqAdded, setIsFaqAdded] = useState(false);
  
    useEffect(() => {
      get({ client_id: clientId }, '/areFaqsAdded').then((res) => {
        if (res && res.success) {
          setIsFaqAdded(res.result);
        }
      });
    }, []);


  const playerRef = useRef(null);

  // ─── Language state ────────────────────────────────────────────────────────
  // 'en' | 'hi'
  const [language, setLanguage] = useState('en');
  const isHindi = language === 'hi';

  // Tracks the current category node so we know skipToPayment for each course carousel
  const [currentCategoryNode, setCurrentCategoryNode] = useState(null);

  const [hasHealthIssue, setHasHealthIssue] = useState(false);

  // Dynamic form state keyed by messageId
  const [activeForms, setActiveForms] = useState({});

  // All form submissions collected; API fired on each submit
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);

  // Contact info collected from the last submitted noHealthIssueForm
  const [collectedContact, setCollectedContact] = useState(null);

  // Payment mini-form state for rcpcod flow (keyed by messageId)
  const [activePaymentForms, setActivePaymentForms] = useState({});

  // Tracks which payment messages have been paid (messageId → true)
  const [paidMessages, setPaidMessages] = useState({});

  const messagesEndRef = useRef(null);
  const chatBodyRef = useRef(null);

  // ─── Scroll ────────────────────────────────────────────────────────────────
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ─── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (messages.length === 0) {
      // Always start with language selection node
      setTimeout(() => addBotMessage(chatFlowData['languageSelect'], {}, false), 500);
    }
  }, []);

  // ─── Sync to Redux ──────────────────────────────────────────────────────────
  useEffect(() => {
    setChatMessagesToStore(messages);
  }, [messages, setChatMessagesToStore]);

  // ─── Cleanup: Stop video on unmount ────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
        playerRef.current.pauseVideo();
      }
    };
  }, []);

  // ─── Course fetch ───────────────────────────────────────────────────────────
  const fetchCourses = async (courseCategoryId) => {
    setLoadingCourses(true);
    try {
      const payload = {
        client_id: clientId,
        client_user_id: clientUserId,
        limit: 100,
        course_category_id: courseCategoryId,
        type: process.env.NODE_ENV,
      };
      const res = await get(payload, '/getCoursesOfCategory');
      if (res && res.success) return res.result?.current_courses || [];
      return [];
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    } finally {
      setLoadingCourses(false);
    }
  };

  // ─── Add bot message ────────────────────────────────────────────────────────
  // langOverride lets bootstrap call always use 'en' for language select node
  const addBotMessage = async (node, overrides = {}, langOverride) => {
    const useHindi = langOverride !== undefined ? langOverride : isHindi;
    setIsTyping(true);

    let coursesData = null;
    if (node.showCourses) {
      coursesData = await fetchCourses(node.course_category_id);
    }

    setTimeout(() => {
      const messageId = Date.now();

      // Resolve display text
      const displayText =
        overrides.text ||
        (useHindi && node.hindi_message ? node.hindi_message : node.message) ||
        '';

      const newMessage = {
        id: messageId,
        type: 'bot',
        text: displayText,
        videoText: useHindi && node.hindi_videoText ? node.hindi_videoText : node.videoText,
        coursesText: useHindi && node.hindi_coursesText ? node.hindi_coursesText : node.coursesText,
        options:
          overrides.options !== undefined
            ? overrides.options
            : (node.options || []).map((opt) => ({
                ...opt,
                displayLabel: useHindi && opt.hindi_label ? opt.hindi_label : opt.label,
              })),
        video: node.video || null,
        showCourses: node.showCourses || false,
        courses: coursesData || [],
        showForm: overrides.showForm || node.showForm || false,
        formConfig: overrides.formConfig || node.formConfig || null,
        showPayment: node.showPayment || false,
        requiresContactForm: node.requiresContactForm || false,
        paymentCourse: overrides.paymentCourse || null,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }),
      };

      // Initialise active form entry
      if (newMessage.showForm && newMessage.formConfig) {
        setActiveForms((prev) => ({
          ...prev,
          [messageId]: { data: buildInitialFormData(newMessage.formConfig), errors: {} },
        }));
      }

      // Initialise payment mini-form entry for rcpcod flow
      if (newMessage.showPayment && newMessage.requiresContactForm) {
        setActivePaymentForms((prev) => ({
          ...prev,
          [messageId]: { data: { name: '', phone: '', city: '' }, errors: {} },
        }));
      }

      setMessages((prev) => [...prev, newMessage]);
      setIsTyping(false);
    }, 800);
  };

  // ─── Inject form message directly ──────────────────────────────────────────
  const injectFormMessage = (node) => {
    setIsTyping(false);
    const messageId = Date.now();
    const displayText = isHindi && node.hindi_message ? node.hindi_message : node.message;
    const formMessage = {
      id: messageId,
      type: 'bot',
      text: displayText,
      options: [],
      showForm: true,
      formConfig: node.formConfig,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
    setActiveForms((prev) => ({
      ...prev,
      [messageId]: { data: buildInitialFormData(node.formConfig), errors: {} },
    }));
    setMessages((prev) => [...prev, formMessage]);
  };

  // ─── Inject payment message ─────────────────────────────────────────────────
  const injectPaymentMessage = (nodeKey, course) => {
    const node = chatFlowData[nodeKey];
    const messageId = Date.now();
    const displayText = isHindi && node.hindi_message ? node.hindi_message : node.message;
    const paymentMessage = {
      id: messageId,
      type: 'bot',
      text: displayText,
      options: (node.options || []).map((opt) => ({
        ...opt,
        displayLabel: isHindi && opt.hindi_label ? opt.hindi_label : opt.label,
      })),
      showPayment: true,
      requiresContactForm: node.requiresContactForm || false,
      paymentCourse: course,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
    if (node.requiresContactForm) {
      setActivePaymentForms((prev) => ({
        ...prev,
        [messageId]: { data: { name: '', phone: '', city: '' }, errors: {} },
      }));
    }
    setMessages((prev) => [...prev, paymentMessage]);
    setIsTyping(false);
  };

  // ─── Option click ───────────────────────────────────────────────────────────
  const handleOptionClick = (option) => {
    // Use displayLabel if available (already resolved), else fall back
    const displayText =
      option.displayLabel || (isHindi && option.hindi_label ? option.hindi_label : option.label);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: displayText,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
    setMessages((prev) => [...prev, userMessage]);

    const nextNodeId = option.nextNode;
    setCurrentNodeId(nextNodeId);

    // ─── Language selection handling ───────────────────────────────────────
    // Detect which language was chosen based on the option label
    if (currentNodeId === 'languageSelect') {
      const chosenHindi = option.label === 'हिंदी';
      setLanguage(chosenHindi ? 'hi' : 'en');
      // Now show welcome in the chosen language
      setTimeout(() => {
        const welcomeNode = chatFlowData['welcome'];
        addBotMessage(welcomeNode, {}, chosenHindi);
      }, 600);
      return;
    }

    if (nextNodeId === 'healthIssueResponse') {
      setHasHealthIssue(true);
    }

    // Track which category node we're in so handleCourseClick knows skipToPayment
    if (chatFlowData[nextNodeId]?.showCourses) {
      setCurrentCategoryNode(nextNodeId);
    }

    // Any node with showForm:true — inject directly
    if (nextNodeId && chatFlowData[nextNodeId]?.showForm) {
      setTimeout(() => injectFormMessage(chatFlowData[nextNodeId]), 600);
      return;
    }

    if (nextNodeId && chatFlowData[nextNodeId]) {
      setTimeout(() => addBotMessage(chatFlowData[nextNodeId]), 600);
    }
  };

  // ─── Course click ───────────────────────────────────────────────────────────
  const handleCourseClick = (course) => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: course.course_title,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
    setSelectedCourse(course);

    const categoryNode = currentCategoryNode ? chatFlowData[currentCategoryNode] : null;
    const skipToPayment = categoryNode?.skipToPayment === true;

    const confirmText = isHindi
      ? `आपने "${course.course_title}" कोर्स चुना है। 🎉`
      : `You have opted for "${course.course_title}" course. 🎉`;

    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        const confirmMessage = {
          id: Date.now(),
          type: 'bot',
          text: confirmText,
          options: [],
          showForm: false,
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        };
        setMessages((prev) => [...prev, confirmMessage]);
        setIsTyping(false);

        if (skipToPayment) {
          setTimeout(() => {
            setIsTyping(true);
            setTimeout(() => injectPaymentMessage('paymentNodeRcpcod', course), 800);
          }, 400);
        } else if (hasHealthIssue) {
          setTimeout(() => {
            setIsTyping(true);
            setTimeout(() => injectFormMessage(chatFlowData['noHealthIssueForm']), 800);
          }, 400);
        } else {
          setTimeout(() => {
            setIsTyping(true);
            setTimeout(() => {
              const healthNode = chatFlowData['askHealthIssues'];
              const healthText =
                isHindi && healthNode.hindi_message ? healthNode.hindi_message : healthNode.message;
              const healthMessage = {
                id: Date.now(),
                type: 'bot',
                text: healthText,
                options: healthNode.options.map((opt) => {
                  const mappedOpt =
                    opt.nextNode === 'showCourseswlc'
                      ? { ...opt, nextNode: 'noHealthIssueForm' }
                      : opt;
                  return {
                    ...mappedOpt,
                    displayLabel:
                      isHindi && mappedOpt.hindi_label ? mappedOpt.hindi_label : mappedOpt.label,
                  };
                }),
                showForm: false,
                timestamp: new Date().toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                }),
              };
              setMessages((prev) => [...prev, healthMessage]);
              setIsTyping(false);
            }, 800);
          }, 400);
        }
      }, 800);
    }, 400);
  };

  // ─── Form field change ──────────────────────────────────────────────────────
  const handleFormChange = (messageId, fieldName, value) => {
    setActiveForms((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        data: { ...prev[messageId].data, [fieldName]: value },
        errors: { ...prev[messageId].errors, [fieldName]: '' },
      },
    }));
  };

  // ─── Form submit ────────────────────────────────────────────────────────────
  const handleFormSubmit = async (messageId, formConfig) => {
    const formState = activeForms[messageId];
    if (!formState) return;

    const errors = validateForm(formConfig, formState.data, isHindi);
    if (Object.keys(errors).length > 0) {
      setActiveForms((prev) => ({
        ...prev,
        [messageId]: { ...prev[messageId], errors },
      }));
      return;
    }

    const submission = {
      formId: formConfig.formId,
      formTitle: formConfig.title,
      submittedAt: new Date().toISOString(),
      data: { ...formState.data },
    };

    const updatedSubmissions = [...allFormSubmissions, submission];
    setAllFormSubmissions(updatedSubmissions);

    try {
      const message = messages.find((msg) => msg.id === messageId);
      const courseId = message?.paymentCourse?.course_id;

      post(
        {
          client_id: clientId,
          client_user_id: clientUserId,
          form_id: submission.formId,
          form_data: JSON.stringify(submission.data),
          course_id: selectedCourse.course_id,
        },
        '/submitChatbotForm',
      );
    } catch (error) {
      console.error('Form submission API error:', error);
    }

    const { name, phone, city } = formState.data;
    if (name || phone || city) {
      setCollectedContact({ name: name || '', phone: phone || '', city: city || '' });
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              showForm: false,
              formSubmitted: true,
              submittedData: formState.data,
              formConfig,
            }
          : msg,
      ),
    );

    setActiveForms((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });

    const afterNode = formConfig.afterSubmitNode || 'formSubmitResponse';

    if (afterNode === 'paymentNode') {
      setTimeout(() => {
        addBotMessage(chatFlowData['formSubmitResponse']);
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => injectPaymentMessage('paymentNode', selectedCourse), 800);
        }, 1000);
      }, 400);
    } else {
      setTimeout(() => addBotMessage(chatFlowData[afterNode]), 400);
    }
  };

  // ─── Payment mini-form change (rcpcod flow) ─────────────────────────────────
  const handlePaymentFormChange = (messageId, fieldName, value) => {
    setActivePaymentForms((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        data: { ...prev[messageId].data, [fieldName]: value },
        errors: { ...prev[messageId].errors, [fieldName]: '' },
      },
    }));
  };

  // ─── Pay Now ────────────────────────────────────────────────────────────────
  const handlePayNow = async (messageId, paymentCourse, requiresContactForm) => {
    setPaidMessages((prev) => ({ ...prev, [messageId]: true }));

    const randomColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    history.push({
      pathname: `/courses/buyCourse/${window.btoa(clientId)}/${window.btoa(
        paymentCourse?.course_id,
      )}`,
      state: { randomColor },
    });
  };

  // ─── Restart ────────────────────────────────────────────────────────────────
  const handleRestartChat = () => {
    setMessages([]);
    setChatMessagesToStore([]);
    setCurrentNodeId('languageSelect');
    setLanguage('en');
    setIsChatOpen(true);
    setSelectedCourse(null);
    setCurrentCategoryNode(null);
    setHasHealthIssue(false);
    setActiveForms({});
    setActivePaymentForms({});
    setAllFormSubmissions([]);
    setCollectedContact(null);
    setPaidMessages({});
    setTimeout(() => addBotMessage(chatFlowData['languageSelect'], {}, false), 300);
  };

  const initializeCall = () => {
    window.location.href = 'tel:+919650010073';
  };

  const formatPrice = (price) => (price === 0 ? 'Free' : `₹${price}`);

  const opts = {
    playerVars: {
      modestbranding: 1,
      rel: 0,
      controls: 0,
      enablejsapi: 1,
      fs: 0,
      iv_load_policy: 3,
      origin: window.location.origin,
      autoplay: 1,
    },
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={window.location.pathname.includes('chatbot') ? { height: '100vh' } : {}}
      className='ChatBot__container'
    >
      {isChatOpen ? (
        <div className='ChatBot__window'>
          {/* Header */}
          <div className='ChatBot__header'>
            <div className='ChatBot__header-left'>
              <button
                onClick={() => (isWrapper ? {} : history.goBack())}
                className='ChatBot__back-btn'
                aria-label='Back'
              >
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M15 18L9 12L15 6'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
              <div className='ChatBot__header-info'>
                <h2 onClick={handleRestartChat} className='ChatBot__header-title'>
                  {isHindi ? 'सहायता सेवा' : 'Support Assistant'}
                </h2>
                <span className='ChatBot__header-status'>
                  <span className='ChatBot__status-dot'></span>
                  {isHindi ? 'ऑनलाइन' : 'Online'}
                </span>
              </div>
            </div>
            <div className='d-flex gap-1 align-items-center'>
            {isFaqAdded && <IconButton className='ChatBot__end-btn' onClick={()=>history.push('/faq')}>
              <HelpOutlined color='var(--primary-blue)' />
            </IconButton>}
            <IconButton className='ChatBot__end-btn' onClick={initializeCall}>
              <Call color='var(--primary-blue)' />
            </IconButton>
            </div>
          </div>

          {/* Chat Body */}
          <div className='ChatBot__body' ref={chatBodyRef}>
            <div className='ChatBot__date-divider'>
              <span>{isHindi ? 'आज' : 'Today'}</span>
            </div>

            {messages.map((message) => (
              <div key={message.id} className='ChatBot__message-wrapper'>
                {message.type === 'bot' ? (
                  <div className='ChatBot__message ChatBot__message--bot'>
                    <div className='ChatBot__message-content'>
                      <p className='ChatBot__message-text'>{message.text}</p>

                      {/* YouTube Video */}
                      {message.video && (
                        <>
                          <p className='ChatBot__message-text'>{message.videoText}</p>
                          <div
                            onClick={() => {
                              if (playerRef?.current?.getPlayerState() === 2) {
                                playerRef?.current?.playVideo();
                              } else {
                                playerRef?.current?.pauseVideo();
                              }
                            }}
                            className='ChatBot__video-container'
                          >
                            {/* <iframe
                              className='ChatBot__video-iframe'
                              src={`https://www.youtube.com/embed/${message.video}`}
                              title='Tutorial Video'
                              frameBorder='0'
                              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                              allowFullScreen
                            /> */}
                            <YouTube
                              videoId={message.video}
                              ref={playerRef}
                              iframeClassName={'chatbotIframe'}
                              style={{ width: '100%' }}
                              opts={opts}
                              allow='accelerometer;  clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                              onReady={(event) => {
                                const player = event.target;
                                playerRef.current = player;

                                event.target.setVolume(100);
                              }}
                              onEnd={() => {}}
                              onStateChange={() => {}}
                            />
                          </div>
                        </>
                      )}

                      {/* Courses Carousel */}
                      {message.showCourses && (
                        <div className='ChatBot__courses-section'>
                          <p className='ChatBot__message-text mt-3'>{message.coursesText}</p>
                          {loadingCourses ? (
                            <div className='ChatBot__courses-loading'>
                              <div className='ChatBot__loading-spinner'></div>
                              <p>{isHindi ? 'कोर्स लोड हो रहे हैं...' : 'Loading courses...'}</p>
                            </div>
                          ) : message.courses && message.courses.length > 0 ? (
                            <div className='ChatBot__courses-carousel'>
                              {message.courses.map((course) => (
                                <div
                                  key={course.course_id}
                                  className='ChatBot__course-card'
                                  onClick={() => handleCourseClick(course)}
                                >
                                  <div className='ChatBot__course-image'>
                                    <img
                                      src={course.course_display_image || thumbnailImage}
                                      alt={course.course_title}
                                      onError={(e) => {
                                        e.target.src = thumbnailImage;
                                      }}
                                    />
                                    {course.bestseller_tag === 'true' && (
                                      <span className='ChatBot__course-badge'>
                                        {isHindi ? 'बेस्टसेलर' : 'Bestseller'}
                                      </span>
                                    )}
                                  </div>
                                  <div className='ChatBot__course-info'>
                                    <h4 className='ChatBot__course-title'>{course.course_title}</h4>
                                    <div className='ChatBot__course-meta'>
                                      {course.language && (
                                        <span className='ChatBot__course-language'>
                                          {course.language.split('~').join(', ')}
                                        </span>
                                      )}
                                      {course.category_names?.length > 0 && (
                                        <span className='ChatBot__course-category'>
                                          {course.category_names[0]}
                                        </span>
                                      )}
                                    </div>
                                    <div className='ChatBot__course-footer'>
                                      <div className='ChatBot__course-pricing'>
                                        {course.discount_price > 0 &&
                                        course.discount_price < course.course_price ? (
                                          <>
                                            <span className='ChatBot__course-price-discounted'>
                                              {formatPrice(course.discount_price)}
                                            </span>
                                            <span className='ChatBot__course-price-original'>
                                              {formatPrice(course.course_price)}
                                            </span>
                                          </>
                                        ) : (
                                          <span className='ChatBot__course-price'>
                                            {formatPrice(course.course_price)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button className='ChatBot__course-btn'>
                                      {isHindi ? 'कोर्स देखें' : 'View Course'}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className='ChatBot__courses-empty'>
                              <p>
                                {isHindi
                                  ? 'अभी कोई कोर्स उपलब्ध नहीं है।'
                                  : 'No courses available at the moment.'}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ✅ Dynamic Inline Form — active */}
                      {message.showForm && message.formConfig && activeForms[message.id] && (
                        <div className='ChatbotForm__container'>
                          {message.formConfig.title && (
                            <h4 className='ChatbotForm__title'>
                              {isHindi && message.formConfig.hindi_title
                                ? message.formConfig.hindi_title
                                : message.formConfig.title}
                            </h4>
                          )}
                          {message.formConfig.fields.map((field) => {
                            const formState = activeForms[message.id];
                            const error = formState.errors[field.name];
                            const fieldLabel =
                              isHindi && field.hindi_label ? field.hindi_label : field.label;
                            const fieldPlaceholder =
                              isHindi && field.hindi_placeholder
                                ? field.hindi_placeholder
                                : field.placeholder;
                            return (
                              <div className='ChatbotForm__field' key={field.name}>
                                <label className='ChatbotForm__label'>{fieldLabel}</label>
                                <input
                                  className={`ChatbotForm__input ${
                                    error ? 'ChatbotForm__input--error' : ''
                                  }`}
                                  type={field.type}
                                  name={field.name}
                                  value={formState.data[field.name]}
                                  onChange={(e) =>
                                    handleFormChange(message.id, field.name, e.target.value)
                                  }
                                  placeholder={fieldPlaceholder}
                                />
                                {error && <span className='ChatbotForm__error'>{error}</span>}
                              </div>
                            );
                          })}
                          <button
                            className='ChatbotForm__submit-btn'
                            onClick={() => handleFormSubmit(message.id, message.formConfig)}
                          >
                            {isHindi && message.formConfig.hindi_submitLabel
                              ? message.formConfig.hindi_submitLabel
                              : message.formConfig.submitLabel || 'Submit'}
                          </button>
                        </div>
                      )}

                      {/* ✅ Submitted form — read-only summary */}
                      {message.formSubmitted && message.submittedData && message.formConfig && (
                        <div className='ChatbotForm__submitted'>
                          <div className='ChatbotForm__submitted-header'>
                            <span className='ChatbotForm__submitted-check'>✅</span>
                            <span className='ChatbotForm__submitted-title'>
                              {isHindi && message.formConfig.hindi_title
                                ? message.formConfig.hindi_title
                                : message.formConfig.title ||
                                  (isHindi ? 'फॉर्म जमा हुआ' : 'Form Submitted')}
                            </span>
                          </div>
                          <div className='ChatbotForm__submitted-grid'>
                            {message.formConfig.fields.map((field) => (
                              <div className='ChatbotForm__submitted-row' key={field.name}>
                                <span className='ChatbotForm__submitted-key'>
                                  {isHindi && field.hindi_label ? field.hindi_label : field.label}
                                </span>
                                <span className='ChatbotForm__submitted-value'>
                                  {message.submittedData[field.name]}
                                  {field.name === 'height'
                                    ? ' cm'
                                    : field.name === 'weight'
                                    ? ' kg'
                                    : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ✅ Payment Card */}
                      {message.showPayment && message.paymentCourse && (
                        <div className='ChatbotPayment__container'>
                          <div className='ChatbotPayment__course-summary'>
                            <img
                              className='ChatbotPayment__course-img'
                              src={message.paymentCourse.course_display_image || thumbnailImage}
                              alt={message.paymentCourse.course_title}
                              onError={(e) => {
                                e.target.src = thumbnailImage;
                              }}
                            />
                            <div className='ChatbotPayment__course-details'>
                              <h4 className='ChatbotPayment__course-title'>
                                {message.paymentCourse.course_title}
                              </h4>
                              <div className='ChatbotPayment__course-pricing'>
                                {message.paymentCourse.discount_price > 0 &&
                                message.paymentCourse.discount_price <
                                  message.paymentCourse.course_price ? (
                                  <>
                                    <span className='ChatbotPayment__price-discounted'>
                                      {formatPrice(message.paymentCourse.discount_price)}
                                    </span>
                                    <span className='ChatbotPayment__price-original'>
                                      {formatPrice(message.paymentCourse.course_price)}
                                    </span>
                                  </>
                                ) : (
                                  <span className='ChatbotPayment__price'>
                                    {formatPrice(message.paymentCourse.course_price)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {!paidMessages[message.id] ? (
                            <button
                              className='ChatbotPayment__pay-btn'
                              onClick={() =>
                                handlePayNow(
                                  message.id,
                                  message.paymentCourse,
                                  message.requiresContactForm,
                                )
                              }
                            >
                              {isHindi ? '💳 अभी Payment करें' : '💳 Pay Now'}
                            </button>
                          ) : (
                            <div className='ChatbotPayment__paid-badge'>
                              {isHindi ? '✅ Payment शुरू हो गई' : '✅ Payment Initiated'}
                            </div>
                          )}
                        </div>
                      )}

                      <span className='ChatBot__message-time'>{message.timestamp}</span>
                    </div>

                    {/* Options */}
                    {message.options?.length > 0 && (
                      <div className='ChatBot__options'>
                        {message.options.map((option, index) => (
                          <button
                            key={index}
                            className='ChatBot__option-btn'
                            onClick={() => handleOptionClick(option)}
                          >
                            {option.displayLabel || option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='ChatBot__message ChatBot__message--user'>
                    <div className='ChatBot__message-content'>
                      <p className='ChatBot__message-text'>{message.text}</p>
                      <span className='ChatBot__message-time'>{message.timestamp}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className='ChatBot__message-wrapper'>
                <div className='ChatBot__message ChatBot__message--bot'>
                  <div className='ChatBot__typing-indicator'>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      ) : (
        <div className='ChatBot__ended'>
          <div className='ChatBot__ended-content'>
            <div className='ChatBot__ended-icon'>
              <svg width='64' height='64' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </div>
            <h3 className='ChatBot__ended-title'>{isHindi ? 'चैट खत्म हुई' : 'Chat Ended'}</h3>
            <p className='ChatBot__ended-text'>
              {isHindi
                ? 'हमसे बात करने के लिए शुक्रिया। उम्मीद है हम आपके काम आए!'
                : 'Thank you for contacting support. We hope we could help you!'}
            </p>
            <button className='ChatBot__restart-btn' onClick={handleRestartChat}>
              {isHindi ? 'नई चैट शुरू करें' : 'Start New Chat'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  clientId: getClientId(state),
  clientUserId: getClientUserId(state),
  chatMessages: getChatMessages(state),
});

const mapDispatchToProps = (dispatch) => ({
  setChatMessagesToStore: (payload) => dispatch(chatbotActions.setChatMessagesToStore(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Chatbot);
